import React from 'react';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Copy, ThumbsUp, ThumbsDown, BarChart3, User, Clock, Zap } from 'lucide-react';
import { cn } from '../../lib/utils';

interface Message {
  id: string;
  content: string;
  type: 'user' | 'assistant' | 'clarification';
  timestamp: string;
  visualizations?: any[];
  followUpQuestions?: string[];
  processingTime?: number;
  tokensUsed?: number;
  clarification?: boolean;
  suggestions?: string[];
}

interface MessageBubbleProps {
  message: Message;
  onFollowUpQuestion?: (question: string) => void;
}

export function MessageBubble({ message, onFollowUpQuestion }: MessageBubbleProps) {
  const isUser = message.type === 'user';
  
  const formatTime = (timestamp: string) => {
    return new Date(timestamp).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(message.content);
  };

  const renderMarkdownContent = (content: string) => {
    // Simple markdown renderer for better formatting
    const lines = content.split('\n');
    const elements: JSX.Element[] = [];
    
    lines.forEach((line, index) => {
      if (line.startsWith('### ')) {
        // H3 headers
        elements.push(
          <h3 key={index} className="text-lg font-semibold mt-4 mb-2 text-blue-600 dark:text-blue-400 text-left">
            {line.replace('### ', '')}
          </h3>
        );
      } else if (line.startsWith('## ')) {
        // H2 headers
        elements.push(
          <h2 key={index} className="text-xl font-bold mt-4 mb-2 text-gray-900 dark:text-white text-left">
            {line.replace('## ', '')}
          </h2>
        );
      } else if (line.startsWith('- **') && line.includes('**:')) {
        // Bold list items with colons
        const match = line.match(/- \*\*(.*?)\*\*: (.*)/);
        if (match) {
          elements.push(
            <div key={index} className="flex mb-2 text-left">
              <span className="font-semibold text-gray-900 dark:text-white mr-2">•</span>
              <div className="flex-1">
                <span className="font-semibold text-gray-900 dark:text-white">{match[1]}:</span>
                <span className="ml-1 text-gray-700 dark:text-gray-300">{match[2]}</span>
              </div>
            </div>
          );
        }
      } else if (line.startsWith('- ')) {
        // Regular list items
        elements.push(
          <div key={index} className="flex mb-1 text-left">
            <span className="text-gray-600 dark:text-gray-400 mr-2">•</span>
            <span className="text-gray-700 dark:text-gray-300 flex-1">{line.replace('- ', '')}</span>
          </div>
        );
      } else if (line.trim() === '') {
        // Empty lines
        elements.push(<div key={index} className="h-2"></div>);
      } else if (line.match(/^\d+\./)) {
        // Numbered lists
        elements.push(
          <p key={index} className="mb-2 text-gray-700 dark:text-gray-300 text-left" dangerouslySetInnerHTML={{ 
            __html: line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') 
          }}></p>
        );
      } else {
        // Regular text with bold formatting
        const formattedLine = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
        elements.push(
          <p key={index} className="mb-2 text-gray-700 dark:text-gray-300 text-left" dangerouslySetInnerHTML={{ __html: formattedLine }}></p>
        );
      }
    });
    
    return elements;
  };

  return (
    <div className={cn('flex', isUser ? 'justify-end' : 'justify-start')}>
      <div className={cn(
        'rounded-xl sm:rounded-lg p-3 sm:p-4 shadow-sm',
        isUser 
          ? 'bg-gradient-to-r from-blue-600 to-blue-900 text-white ml-4 sm:ml-12 mr-2 max-w-[85%] sm:max-w-3xl'
          : 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 ml-2 mr-4 sm:mr-12 max-w-[85%] sm:max-w-3xl'
      )}>
        {/* Message Header */}
        <div className="flex items-center space-x-2 mb-2">
          <div className={cn(
            'w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0',
            isUser ? 'bg-white bg-opacity-20' : 'bg-gray-100 dark:bg-gray-700'
          )}>
            {isUser ? (
              <User className="w-4 h-4" />
            ) : (
              <BarChart3 className="w-4 h-4 text-blue-600 dark:text-blue-400" />
            )}
          </div>
          <span className={cn(
            'text-sm font-medium flex-shrink-0',
            isUser ? 'text-white' : 'text-gray-900 dark:text-white'
          )}>
            {isUser ? 'You' : 'Analytics Agent'}
          </span>
          <span className={cn(
            'text-xs flex-shrink-0',
            isUser ? 'text-white text-opacity-70' : 'text-gray-500 dark:text-gray-400'
          )}>
            {formatTime(message.timestamp)}
          </span>
        </div>

        {/* Message Content */}
        <div className={cn(
          'max-w-none text-left leading-relaxed',
          isUser ? 'text-white' : 'text-gray-900 dark:text-white'
        )}>
          {isUser ? (
            <p className="whitespace-pre-wrap text-white text-left text-base sm:text-sm leading-relaxed">{message.content}</p>
          ) : (
            <div className="space-y-2 text-left text-base sm:text-sm leading-relaxed">
              {renderMarkdownContent(message.content)}
            </div>
          )}
        </div>

        {/* Visualizations */}
        {message.visualizations && message.visualizations.length > 0 && (
          <div className="mt-4 space-y-3 sm:space-y-4">
            {message.visualizations.map((viz, index) => (
              <div key={index} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 sm:p-4">
                <h4 className="font-medium text-gray-900 dark:text-white mb-2 text-sm sm:text-base">{viz.title}</h4>
                {/* TODO: Implement actual chart rendering */}
                <div className="h-48 sm:h-64 bg-gray-200 dark:bg-gray-600 rounded flex items-center justify-center">
                  <span className="text-gray-500 dark:text-gray-400 text-sm">Chart: {viz.type}</span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Follow-up Questions */}
        {message.followUpQuestions && message.followUpQuestions.length > 0 && (
          <div className="mt-4">
            <p className={cn(
              'text-sm mb-3',
              isUser ? 'text-white text-opacity-80' : 'text-gray-600 dark:text-gray-400'
            )}>Suggested follow-up questions:</p>
            <div className="space-y-2">
              {message.followUpQuestions.map((question, index) => (
                <Button
                  key={index}
                  variant="outline"
                  size="sm"
                  onClick={() => onFollowUpQuestion?.(question)}
                  className={cn(
                    "text-left justify-start w-full min-h-[40px] sm:min-h-[36px] p-3 sm:p-2 text-sm rounded-lg transition-all duration-200",
                    isUser 
                      ? "border-white border-opacity-30 text-white hover:bg-white hover:bg-opacity-10" 
                      : "border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
                  )}
                >
                  <span className="leading-relaxed">{question}</span>
                </Button>
              ))}
            </div>
          </div>
        )}

        {/* Message Actions */}
        {!isUser && (
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mt-4 pt-3 border-t border-gray-200 dark:border-gray-700 space-y-3 sm:space-y-0">
            <div className="flex items-center space-x-2">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleCopy} 
                className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white h-9 px-3 rounded-lg"
              >
                <Copy className="w-4 h-4 mr-2" />
                <span className="text-sm">Copy</span>
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white h-9 px-3 rounded-lg"
              >
                <ThumbsUp className="w-4 h-4 mr-2" />
                <span className="text-sm">Helpful</span>
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white h-9 px-3 rounded-lg"
              >
                <ThumbsDown className="w-4 h-4 mr-2" />
                <span className="text-sm hidden sm:inline">Not helpful</span>
                <span className="text-sm sm:hidden">Poor</span>
              </Button>
            </div>
            
            {/* Message Metadata - Mobile Optimized */}
            {(message.processingTime || message.tokensUsed) && (
              <div className="flex flex-col sm:flex-row sm:items-center space-y-1 sm:space-y-0 sm:space-x-4 text-xs text-gray-500 dark:text-gray-400">
                {message.processingTime && (
                  <span className="flex items-center">
                    <Clock className="w-3 h-3 mr-1" />
                    {(message.processingTime / 1000).toFixed(1)}s
                  </span>
                )}
                {message.tokensUsed && (
                  <span className="flex items-center">
                    <Zap className="w-3 h-3 mr-1" />
                    {message.tokensUsed} tokens
                  </span>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}